//Dealer Franchisee subclass views
import {
  AskConfirmation,
  confirmationButtons,
  ConfirmationButtonType
} from '../../../../webmodule-common/other/ui/modal-confirmation';
import { cache } from '../../cache-impl/cache-registry';
import { createNewQuote } from '../quote-creator';
import { customElement, state } from 'lit/decorators.js';
import { defaultStagedThreshold, EventCancellation } from '../../../../webmodule-common/other/general/staged-event';
import { displaySupplierTACNotApprovedMsg } from '../../../v6config/supplier-services';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
import { EventSnippet, NullPromise } from '../../../../webmodule-common/other/ui/events';
import { FranchiseeApi } from '../../../api/franchisee-api';
import { getApiFactory } from '../../../api/api-injector';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { getQuoteContainer, getQuoteContainerManager } from '../quote-ui-adapter';
import { getQuoteNumberFormatted, getQuoteStatus } from '../../../quotes/data/quote-helper-functions';
import { getSupplier } from '../supplier-selector';
import { html, TemplateResult } from 'lit';
import { isEmptyOrSpace, validId } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { IStaleData } from '../../../../webmodule-common/other/stale-data';
import { ItemReference } from '../../../../webmodule-common/cache/definitions/cache-item-reference';
import { moneyToTemplateResult } from '../../../../webmodule-common/other/currency-formatter';
import { notificationSignal, staleIcon } from '../../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { ProjectResourceLink } from '../../cache-impl/resource-link';
import { QuoteCacheData } from '../../cache-impl/cache-data';
import { QuoteContainer, QuoteContainerManager } from '../../../quotes/data/quote-container';
import { QuoteListView, QuoteSummaryTable, QuoteSummaryTableOptions } from '../../../quotes/views/quote-list-view';
import {
  QuoteState,
  ResultGetQuoteSummary,
  ViewQuoteSummary,
  ViewQuoteTotalByState
} from '../../../api/dealer-api-interface-quote';
import { resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { resourceProject } from '../../../projects/ui/launcher';
import { SSEQuote } from '../../../quotes/data/sse-quote';
import { stagedExecution } from '../../../../webmodule-common/other/general/event-source-client';
import { tlang } from '../../../../webmodule-common/other/language/lang';
import { userDataStore } from '../../common/current-user-data-store';
import {
  WebmoduleChangeEvent,
  WebModuleLitTable,
  WebModuleLitTableColumnDef,
  WebmoduleRadioGroup,
  WebmoduleSelectEvent
} from '../../../../webmodule-common/components/src/webmodule-components';
import { when } from 'lit/directives/when.js';
import { WMEventSource } from '../../../api/event-source';

const numberOfDaysHistory = 30;

@customElement('wm-franchiseequotesummarytable')
class FranchiseeQuoteSummaryTable extends QuoteSummaryTable {
  private projectCache = cache().project;
  private quoteCache = cache().quote;
  private linkCache = cache().projectResourceLink;
  private showAllQuotes = true;
  private count = 1;
  protected staleData: IStaleData;

  constructor(options: QuoteSummaryTableOptions) {
    super(options);
    //this blocks loading of any data until the first time we want it to occur.

    this.staleData = options.stale ?? { exists: () => false };
    this.sortAscending = false;
  }

  isDataLoadDelayed() {
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getColumns(): WebModuleLitTableColumnDef[] {
    const columns: WebModuleLitTableColumnDef[] = [];
    columns.push({
      title: tlang`%%quote%% No.`,
      fieldName: 'quoteNumber',
      sortable: true,
      isDefaultSortDirectionAscending: false,
      classes: 'colpx-130 colpxmax-130 quote-number',
      displayValue: (_table: WebModuleLitTable, item: unknown) => {
        const quoteSummary = item as ViewQuoteSummary;
        return this.stale(quoteSummary)
          ? // eslint-disable-next-line @typescript-eslint/no-base-to-string
            `${staleIcon(false)}${this.quoteLink(quoteSummary, getQuoteNumberFormatted(quoteSummary))}`
          : this.quoteLink(quoteSummary, getQuoteNumberFormatted(quoteSummary));
      }
    });

    columns.push(
      ...[
        {
          title: tlang`%%quote%% Title`,
          fieldName: 'title',
          sortable: true,
          classes: 'colpx-400 quote-title',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.quoteLink(quoteSummary, quoteSummary.title);
          }
        },
        {
          title: tlang`%%project%% Title`,
          fieldName: 'id',
          sortable: false,
          classes: 'colpx-270 colpxmax-270 quote-project-number',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getProjectLink(quoteSummary.id);
          }
        },
        {
          title: tlang`%%client%%`,
          fieldName: 'quoteCustomerId',
          sortable: false,
          classes: 'colpx-240 colpxmax-240 quote-client-name',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getCustomerLink(quoteSummary.quoteCustomerId);
          }
        },
        {
          title: tlang`%%author%%`,
          fieldName: 'assignedToUserId',
          sortable: false,
          classes: 'colpx-200 colpxmax-200 quote-created-by',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getUserDisplayValue(quoteSummary.assignedToUserId);
          }
        },
        {
          title: tlang`Last Modified`,
          fieldName: 'lastModifiedDate',
          sortable: false,
          classes: 'colpx-180 colpxmax-180 quote-modified-date',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            const dt = new Date(quoteSummary.lastModifiedDate ?? '');
            return html`${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
          }
        },
        {
          title: tlang`Status`,
          fieldName: 'state',
          sortable: false,
          classes: 'colpx-130 colpxmax-130 quote-status no-pseudo',

          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return getQuoteStatus(quoteSummary.state, true);
          }
        },
        {
          title: tlang`Net Amount`,
          fieldName: 'calculatedNetTotal',
          sortable: false,
          classes: 'colpx-110 colpxmax-110 alignment-right quote-amount no-pseudo',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return moneyToTemplateResult(quoteSummary.calculatedNetTotal);
          }
        },
        {
          title: html` <webmodule-icon library="fa" name="fas-bars"></webmodule-icon>`,
          fieldName: 'id',
          sortable: false,
          classes: 'colpx-50 colpxmax-50 item-menu',
          displayValue: (_table: WebModuleLitTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.ellipsisMenu(quoteSummary);
          }
        }
      ]
    );
    return columns;
  }

  stale(row: ViewQuoteSummary) {
    return this.staleData.exists(row.id);
  }

  override enableAdvancedFiltering(): boolean {
    return true;
  }

  override advancedFilterTemplate(): TemplateResult {
    this.count++;
    const createRadioButton = (text: string, input: boolean) => {
      return html` <webmodule-radio value="${input}">${text}</webmodule-radio>`;
    };

    return html`
      <webmodule-radio-group
        name="filtergroup${this.count.toString()}"
        class="quote-author-filter"
        value="${this.showAllQuotes}"
        inline
        size="small"
        @webmodule-change=${(e: WebmoduleChangeEvent) => this.onQuoteAuthorChanged(e)}
      >
        ${createRadioButton(tlang`All !!quote!!`, true)} ${createRadioButton(tlang`My !!quote!!`, false)}
      </webmodule-radio-group>
    `;
  }

  async onMenuItemSelected(event: WebmoduleSelectEvent, quoteSummary: ViewQuoteSummary) {
    if (event.detail.item.value == 'delete') {
      if (
        await AskConfirmation(
          tlang`Are you sure you'd like to delete the %%quote%%?`,
          confirmationButtons[ConfirmationButtonType.yesNo]
        )
      )
        await this.deleteQuoteItem(quoteSummary);
    } else if (event.detail.item.value == 'copy') {
      await this.copyQuoteItem(quoteSummary);
    }
  }

  ellipsisMenu(quoteSummary: ViewQuoteSummary): TemplateResult {
    return html` <webmodule-dropdown
      placement="bottom-end"
      hoist
      @webmodule-select=${(e: WebmoduleSelectEvent) => this.onMenuItemSelected(e, quoteSummary)}
    >
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        ${when(
          !(quoteSummary.state == QuoteState.Draft),
          () => '',
          () =>
            html` <webmodule-menu-item class="action-delete" value="delete">
              <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
              Delete
            </webmodule-menu-item>`
        )}

        <webmodule-menu-item class="action-copy" value="copy">
          <webmodule-icon slot="prefix" library="fa" name="fas-copy"></webmodule-icon>
          Copy
        </webmodule-menu-item>
      </webmodule-menu>
    </webmodule-dropdown>`;
  }

  async rowClicked(e: CustomEvent<{ table: WebModuleLitTable; item: ViewQuoteSummary }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.eventHandler(async () => {});
  }

  protected getPreFetched(results: ResultGetQuoteSummary): Promise<void>[] {
    const cachePrefetch = super.getPreFetched(results);

    const updateFromSummaries: ((items: ViewQuoteSummary[]) => Promise<ItemReference<QuoteCacheData>[]>) | undefined = (
      this.quoteCache as any
    ).updateFromSummaries.bind(this.quoteCache);
    if (updateFromSummaries) {
      //this is the optimized efficient method
      const p = updateFromSummaries(results.quoteSummary.results).then(items => {
        const quoteIds = items.map(x => x.id);

        return this.linkCache.preFetch(quoteIds).then(() => {
          const projectIds: string[] = [];
          results.quoteSummary.results.forEach(summary => {
            const projectId = this.linkCache.getLocalData(summary.id)?.projectId ?? emptyGuid;
            if (validId(projectId)) projectIds.push(projectId);
          });
          return this.projectCache.preFetch(projectIds);
        });
      });

      cachePrefetch.push(p);
    } else {
      //less optimized fallback
      const quoteIds = results.quoteSummary.results.map(x => x.id);

      cachePrefetch.push(
        this.quoteCache
          .preFetch(quoteIds)
          .then(() => {
            return this.linkCache.getMany(quoteIds);
          })
          .then((data: ItemReference<ProjectResourceLink>[] | null) => {
            const projectIds = data?.map(x => x.data.projectId) ?? [];
            return this.projectCache.preFetch(projectIds);
          })
      );
    }
    return cachePrefetch;
  }

  protected async onQuoteAuthorChanged(e: WebmoduleChangeEvent) {
    const target = e.target as WebmoduleRadioGroup | null;

    if (target === null) return;

    const groupValue = target.value;

    if (groupValue === 'true') {
      this.showAllQuotes = true;
      this.assignedToUserId = null;
    } else {
      this.assignedToUserId = getCurrentUser()?.id ?? null;
      this.showAllQuotes = false;
    }

    await this.refreshData();
    this.requestUpdate();
  }

  afterReload() {
    this.dataIsStale = false;
  }

  protected getProjectLink(id: string) {
    if (id == emptyGuid) return '';

    const link = this.linkCache.getLocalData(id);

    if (!link) return '';

    const project = this.projectCache.getLocal(link.projectId);

    return project
      ? html`<a class="project-link" href="${resolveURL(resourceProject, project.id)}" data-projectid="${project.id}"
          >${project.data.projectSummary.title}</a
        >`
      : '';
  }
}

@customElement('wm-franchiseequotelistview')
export class FranchiseeQuoteListView extends QuoteListView {
  franchiseeApi: FranchiseeApi = getApiFactory().franchisee();
  @state()
  quoteTotals: ViewQuoteTotalByState[] | null = null;
  private staleQuotes: string[] = [];

  async init() {
    await this.updatePageTotals();
    const table = this.pageControl.activePage?.data as QuoteSummaryTable;
    if (table) await table.refreshData(1);
  }

  async doAfterQuoteEdit() {
    await this.updatePageTotals();
  }

  private _eventCancel: EventCancellation = new EventCancellation();
  private _eventQuoteUpdated = stagedExecution({
    threshold: defaultStagedThreshold,
    cancelToken: this._eventCancel,
    eventFinally: () => notificationSignal(false),
    event: async () => {
      this.updatePageTotals();
      this.tables.forEach(t => (t.dataIsStale = true));
      const table = this.pageControl.activePage?.data as QuoteSummaryTable;
      this.staleQuotes = [];
      if (table) {
        await table.refreshData();
        table.dataIsStale = false;
        //  fireQuickInformationToast(tlang`Your Quotes have been refreshed`);
      }
      //reload the page.. we dont know what moved, or where, or what state its in
    },
    eventTriggered: (_data: SSEQuote) => {
      notificationSignal(true);
      //fireQuickInformationToast(tlang`Updates are coming soon`);
    },
    eventActioned: (data: SSEQuote) => {
      if (!this.staleQuotes.includes(data.id)) this.staleQuotes.push(data.id);
      const table = this.pageControl.activePage?.data as QuoteSummaryTable;
      table.refreshData();
      //fireQuickInformationToast(tlang`More Updates are coming, refreshing soon`);
    }
  });

  connectedCallback(): void {
    super.connectedCallback();
    WMEventSource.getInstance().addEventListener(WMEventSource.quote, this._eventQuoteUpdated);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    WMEventSource.getInstance().removeEventListener(WMEventSource.quote, this._eventQuoteUpdated);
  }

  protected createQuoteContainerManager(quoteContainer: QuoteContainer): QuoteContainerManager {
    return getQuoteContainerManager(quoteContainer);
  }

  protected async updatePageTotals() {
    const ownerId = await this.quoteOwnerId();
    const totals = await this.quoteApi.getQuoteTotalsByState({
      quoteOwnerId: ownerId,
      options: [
        {
          // eslint-disable-next-line no-bitwise
          state: QuoteState.Draft | QuoteState.Active,
          numberOfDaysHistory: null
        },
        {
          state: QuoteState.Draft,
          numberOfDaysHistory: null
        },
        {
          state: QuoteState.Active,
          numberOfDaysHistory: null
        },
        {
          // eslint-disable-next-line no-bitwise
          state: QuoteState.Issued | QuoteState.IssuePending,
          numberOfDaysHistory: null
        },
        {
          state: QuoteState.Accepted,
          numberOfDaysHistory: null
        },
        {
          // eslint-disable-next-line no-bitwise
          state: QuoteState.Accepted_AssignedToReviewer | QuoteState.SupplierReviewPending,
          numberOfDaysHistory: null
        },
        {
          state: QuoteState.SupplierReviewed,
          numberOfDaysHistory: null
        },
        {
          state:
            // eslint-disable-next-line no-bitwise
            QuoteState.Approved |
            QuoteState.Rejected |
            QuoteState.Accepted_RefusedBySupplier |
            QuoteState.Cancelled |
            QuoteState.Lapsed,
          numberOfDaysHistory: numberOfDaysHistory
        }
      ]
    });
    if (totals) this.quoteTotals = totals.totals.filter(x => x.quoteOwnerId === ownerId);
    this.pageControl.requestUpdate();
  }

  protected createQuoteContainer(quoteId: string): QuoteContainer {
    return getQuoteContainer(quoteId);
  }

  //this can be overridden in sub class to replace the table with different column/view etc
  protected quoteSummaryTableFactory(options: QuoteSummaryTableOptions): FranchiseeQuoteSummaryTable {
    return new FranchiseeQuoteSummaryTable(options);
  }

  beforeRefresh() {
    this.staleQuotes = [];
  }

  afterRefresh() {
    this._eventCancel.cancel();
  }

  async dispose(): Promise<void> {
    await super.dispose();
    this._eventCancel.cancel();
  }

  protected createSummaryTable(
    title: EventSnippet,
    quoteStates: QuoteState,
    pageFragment: string,
    numberOfDays?: number
  ): QuoteSummaryTable {
    return this.quoteSummaryTableFactory({
      quoteOwnerId: this.quoteOwnerId,
      quoteStates: quoteStates,
      numberOfDaysHistory: numberOfDays,
      customerCache: this.customerCache,
      userProfileCache: this.userProfileCache,
      title: title,
      copyQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.copyQuote(quoteSummary);
      },
      deleteQuoteEvent: async (quoteSummary: ViewQuoteSummary) => {
        await this.deleteQuote(quoteSummary);
      },
      pageFragment: pageFragment,
      stale: { exists: (id: string) => this.staleQuotes.includes(id) }
    });
  }

  //override this in a subclass to change the design, and order of the tables
  protected initializeTables(): QuoteSummaryTable[] {
    const title = (caption: string, state: QuoteState, hideTotal?: boolean) => {
      let total = 0;
      let count = 0;
      this.quoteTotals
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison,no-bitwise
        ?.filter(x => (state & x.quoteState) === x.quoteState)
        .forEach(x => {
          total += x.total;
          count += x.quoteCount;
        });
      return html`${caption} (${count})${hideTotal ? html`` : moneyToTemplateResult(total)}`;
    };
    const makeTable = (
      titleStr: string,
      state: QuoteState,
      pageFragment: string,
      numberOfDays?: number,
      hideTotal?: boolean
    ) => {
      return this.createSummaryTable(() => title(titleStr, state, hideTotal), state, pageFragment, numberOfDays);
    };

    return [
      // eslint-disable-next-line no-bitwise
      makeTable(tlang`All Current`, QuoteState.Draft | QuoteState.Active, 'allCurrent', undefined, true),
      makeTable(tlang`Draft`, QuoteState.Draft, 'draft', undefined, true),
      makeTable(tlang`Active`, QuoteState.Active, 'active', undefined, true),

      // eslint-disable-next-line no-bitwise
      makeTable(tlang`Issued`, QuoteState.Issued | QuoteState.IssuePending, 'issued', undefined, true),

      makeTable(tlang`Accepted`, QuoteState.Accepted, 'accepted', undefined, true),
      makeTable(
        tlang`Under Review`,
        // eslint-disable-next-line no-bitwise
        QuoteState.Accepted_AssignedToReviewer | QuoteState.SupplierReviewPending,
        'underreview',
        undefined,
        true
      ),
      makeTable(tlang`Reviewed`, QuoteState.SupplierReviewed, 'reviewed', undefined, true),

      makeTable(
        tlang`Archived`,
        // eslint-disable-next-line no-bitwise
        QuoteState.Approved |
          QuoteState.Rejected |
          QuoteState.Accepted_RefusedBySupplier |
          QuoteState.Cancelled |
          QuoteState.Lapsed,
        'archived',
        numberOfDaysHistory,
        true
      )
    ];
  }

  protected async createNewQuote(): NullPromise<QuoteContainer> {
    const supplier = await getSupplier();
    if (isEmptyOrSpace(supplier.supplierId)) return null;
    if (!userDataStore.supplierTACApproved(supplier.supplierId)) {
      await displaySupplierTACNotApprovedMsg(supplier, true);
      return null;
    }
    const owner = await this.quoteOwnerId();

    return createNewQuote({ supplierId: supplier.supplierId, owner: owner });
  }
}
